import {
  ColDef,
  GetContextMenuItemsParams,
  GridApi,
  GridOptions,
  MenuItemDef,
  Module,
} from '@ag-grid-community/core'
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel'
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core'
import {
  disableChangeDetectionOnScroll,
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  getFilterOnCellContextMenu,
  heliosClientSideRowConfig,
} from '@helios/ag-grid-config'
import {
  CreateTransactionsModalService,
  TransactionModalMode,
} from '@helios/create-transactions-modal'
import {
  CreateEditTransAction,
  RootState,
  selectCreateEditTransActions,
} from '@helios/state-management'
import { DensityLevel, UserPreferencesService } from '@helios/user-preferences'
import { prettyFormatNumber } from '@helios/utils'
import { Store } from '@ngrx/store'
import { NfColumnTypes } from '@northfork/ag-grid'
import { composeFieldPath } from '@northfork/basics'
import { Subject } from 'rxjs'
import { take, takeUntil, tap } from 'rxjs/operators'
import {
  HistoricalBalanceComparison,
  HistoricalBalanceComparisonExtToExt,
  HistoricalBalanceComparisonMerged,
} from '../model/historical-balance-comparison.model'
import { Position, PositionTypeEnum } from '../model/positions.model'
import {
  PositionHistoryExtToExtRequest,
  PositionHistoryRequest,
  PositionsService,
} from '../services/positions.service'

const historicalBalanceComparisonPath = composeFieldPath(
  [] as HistoricalBalanceComparison[],
)

const historicalBalanceComparisonPathExtToExt = composeFieldPath(
  [] as HistoricalBalanceComparisonExtToExt[],
)

/**
 * Number formatter to 4 decimal place
 */
export function columnValueFormatter(value: unknown) {
  if (value === 0) {
    // Return itself just in case it's a zero
    return '0'
  }

  if (!value) {
    return ''
  }

  if (!isNaN(Number(value))) {
    return prettyFormatNumber(Number(value), 4)
  }

  return value
}

@Component({
  selector: 'hs-historical-balance-comparison',
  templateUrl: './historical-balance-comparison-panel.component.html',
  styleUrls: ['./historical-balance-comparison-panel.component.scss'],
})
export class HistoricalBalanceComparisonPanelComponent
  implements OnDestroy, OnInit
{
  _position?: Position
  @Input() set position(value: Position | undefined) {
    if (this._position != value) {
      this._position = value
      this.onlyDifferences = false
      this.clearedOnly = this._position?.posTypeCode === 'Cash'
      this.isExternalToExternalComparison =
        this._position?.isExternalToExternalModeSelected ?? false
      this.reloadData()
    }
  }

  @Input() set rowsUpdated(value: string[] | null) {
    if (!this._position || !value?.includes(this._position.positionId)) {
      return
    }
    this.reloadData()
  }

  data$ = new Subject<HistoricalBalanceComparisonMerged[]>()
  private readonly destroySubject$ = new Subject<void>()

  onlyDifferences = false
  clearedOnly = false
  isExternalToExternalComparison = false
  createEditTransActions: string[] = []

  propertiesTitle = 'Table Properties'

  readonly gridModules: Module[] = [FiltersToolPanelModule]

  /** @internal */
  gridApi?: GridApi
  private readonly columnDefs = this.createColumnDefs()

  /** @internal */
  readonly gridOptions: GridOptions = this.createGridOptions()

  constructor(
    private readonly createTransactionsModalService: CreateTransactionsModalService,
    private readonly positionsService: PositionsService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly preferences: UserPreferencesService,
    private readonly store: Store<RootState>,
  ) {}

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
    this.gridApi?.destroy()
  }

  ngOnInit(): void {
    this.store
      .select(selectCreateEditTransActions)
      .pipe(take(1))
      .subscribe(actions => {
        this.createEditTransActions = actions ?? []
      })
  }

  //#region GridConfig
  private createGridOptions(): GridOptions {
    return {
      ...heliosClientSideRowConfig,
      onGridReady: ({ api }) => {
        this.gridApi = api
        this.preferences
          .getDensityLevel()
          .pipe(takeUntil(this.destroySubject$))
          .subscribe(level =>
            enableHeliosGridDensityLevel(api, DensityLevel.Compact === level),
          )
        enableHeliosCopyBehavior(api)
      },

      getContextMenuItems: params => this.getContextMenuItems(params),
      columnDefs: this.columnDefs,
      ...disableChangeDetectionOnScroll(this.changeDetector),
    }
  }

  /** @internal  */
  createColumnDefs(): ColDef[] {
    return this.isExternalToExternalComparison
      ? [
          {
            field: historicalBalanceComparisonPathExtToExt('date'),
            type: NfColumnTypes.Date,
            sort: 'desc',
          },
          {
            field: historicalBalanceComparisonPathExtToExt(
              'externalUnitsOtherDataSource',
            ),
            headerValueGetter: () =>
              this._position?.extPosSourceType?.toLowerCase() == 'trading'
                ? 'CUSTODY'
                : this._position?.extPosSourceType?.toLowerCase() == 'custody'
                ? 'TRADING'
                : 'External',
            valueGetter: ({ data }) =>
              columnValueFormatter(data.externalUnitsOtherDataSource),
            headerName:
              this._position?.extPosSourceType?.toLowerCase() == 'TRADING'
                ? 'CUSTODY'
                : this._position?.extPosSourceType?.toLowerCase() == 'CUSTODY'
                ? 'TRADING'
                : 'External',
          },
          {
            field: historicalBalanceComparisonPathExtToExt(
              'externalUnitsSelected',
            ),
            headerValueGetter: () =>
              this._position?.extPosSourceType ?? 'External',
            valueGetter: ({ data }) =>
              columnValueFormatter(data.externalUnitsSelected),
            headerName: this._position?.extPosSourceType ?? 'External',
          },
          {
            field: historicalBalanceComparisonPathExtToExt('diff'),
            valueGetter: ({ data }) => columnValueFormatter(data.diff),
            headerName: 'Difference',
            cellClass: params => {
              return Math.abs(Math.trunc(params.value * 1000) / 1000) >= 0.005
                ? ' text-danger'
                : ''
            },
          },
        ]
      : [
          {
            field: historicalBalanceComparisonPath('date'),
            type: NfColumnTypes.Date,
            sort: 'desc',
          },
          {
            field: historicalBalanceComparisonPath('enteredCwUnits'),
            valueGetter: ({ data }) =>
              columnValueFormatter(data.enteredCwUnits),
            headerName: 'CWAN',
          },
          {
            field: historicalBalanceComparisonPath('externalUnits'),
            headerValueGetter: () =>
              this._position?.extPosSourceType ?? 'External',
            valueGetter: ({ data }) => columnValueFormatter(data.externalUnits),
            headerName: this._position?.extPosSourceType ?? 'External',
          },
          {
            field: historicalBalanceComparisonPath('diff'),
            valueGetter: ({ data }) => columnValueFormatter(data.diff),
            headerName: 'Difference',
            cellClass: params => {
              return Math.abs(Math.trunc(params.value * 1000) / 1000) >= 0.005
                ? ' text-danger'
                : ''
            },
          },
        ]
  }

  /** Right-click menu */
  getContextMenuItems(params: GetContextMenuItemsParams) {
    const data = params?.node?.data ?? ({} as Position)
    const createAction =
      this._position?.posTypeCode === 'Position'
        ? CreateEditTransAction.CreateTrade
        : CreateEditTransAction.CreateCashEntry
    const canCreateTx = this.createEditTransActions.includes(createAction)
    const result: (string | MenuItemDef)[] = [
      {
        name:
          this._position?.posTypeCode == 'Position'
            ? 'Create Trade'
            : 'Create Cash Entry',
        action: () => this.createTransactionFromHistoricalBalance(data),
        disabled: !canCreateTx,
        tooltip: !canCreateTx
          ? 'You do not have permission to create this transaction type'
          : '',
      },
      ...getFilterOnCellContextMenu(params),
    ]
    return result
  }
  //#endregion GridConfig

  createTransactionFromHistoricalBalance(
    pos: HistoricalBalanceComparisonMerged,
  ) {
    const positionData: { tranType: string; diff: number } =
      this.getTransactionTypeAndDiff(pos)
    if (this._position != undefined)
      this.createTransactionsModalService.toggleCreateTransactionModal(
        positionData.tranType,
        {
          modalMode: TransactionModalMode.CreateFromPosition,
          transType: positionData.tranType,
          positionData: {
            posType: this._position.posTypeCode,
            date: pos.date,
            assetId: this._position.secIdentifier,
            unitDifference: positionData.diff ?? 0.0,
            currencyCode: this._position.currencyCode,
            securityId: this._position.secId,
            price: this._position.secTypeCode === 'MMFUND' ? '1.0' : undefined,
            collateralBroker: this._position.collateralBroker,
          },
        },
      )
  }
  getTransactionTypeAndDiff(pos: HistoricalBalanceComparisonMerged) {
    if (this._position?.posTypeCode === PositionTypeEnum.POSITION) {
      return { tranType: 'SELL', diff: pos.diff }
    } else if (this._position?.posTypeCode === PositionTypeEnum.MARGIN) {
      if (pos.diff < 0) {
        return { tranType: 'MVI', diff: -1 * pos.diff }
      } else {
        return { tranType: 'MVO', diff: -1 * pos.diff }
      }
    } else {
      return { tranType: 'TRN', diff: pos.diff }
    }
  }
  updatePropertiesTitle() {
    let title = ''
    title += this.onlyDifferences
      ? 'Only comparisons with NO differences. '
      : ''
    if (this.clearedOnly && this._position?.posTypeCode === 'Cash')
      title += 'Comparisons based off cleared date. '

    if (title === '') title = 'No Table Properties set'

    this.propertiesTitle = title
  }

  reloadData() {
    if (this.gridApi) {
      this.gridApi?.setColumnDefs(this.createColumnDefs())
      this.gridApi?.refreshHeader()
    } else {
      this.gridOptions.columnDefs = this.createColumnDefs()
    }
    this.updatePropertiesTitle()
    this.setDefaultSorting()

    if (this._position == null) this.data$.next([])
    else {
      this.gridApi?.showLoadingOverlay()
      this.isExternalToExternalComparison
        ? this.positionsService
            .getHistoricalBalanceComparisonsExtToExt({
              positionIdSelected: this._position.positionId,
              positionIdOtherDataSource: this.getOtherSourcePositionId(
                this._position.positionId,
              ),
              clearedOnly: this.clearedOnly,
              onlyDifferences: this.onlyDifferences,
            } as PositionHistoryExtToExtRequest)
            .pipe(
              take(1),
              tap(data => this.data$.next(data)),
            )
            .subscribe()
        : this.positionsService
            .getHistoricalBalanceComparisons({
              positionId: this._position.positionId,
              clearedOnly: this.clearedOnly,
              onlyDifferences: this.onlyDifferences,
            } as PositionHistoryRequest)
            .pipe(
              take(1),
              tap(data => this.data$.next(data)),
            )
            .subscribe()
    }
  }

  setDefaultSorting() {
    this.gridApi?.setSortModel([
      {
        colId: this.isExternalToExternalComparison
          ? historicalBalanceComparisonPath('date')
          : historicalBalanceComparisonPathExtToExt('date'),
        sort: 'desc',
      },
    ])
  }

  getOtherSourcePositionId(positionId: string): string {
    return positionId.includes('-CUSTODY')
      ? positionId.replace('-CUSTODY', '-TRADING')
      : positionId.replace('-TRADING', '-CUSTODY')
  }
}
